import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { CheckCircle, XCircle, AlertCircle, Clock, DollarSign, User, Hash, Calendar } from 'lucide-react';
import { transactionsService } from '../../services/transactionsServices';

const TransactionListing = () => {
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState({
        transaction_type: '',
        status: '',
        network: ''
    });

    useEffect(() => {
        fetchTransactions();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [search, filters, transactions]);

    const fetchTransactions = async () => {
        try {
            const response = await transactionsService.getTransactionsList({
                page: 1,
                limit: 1000, // Get all transactions for client-side filtering
                user_id: '',
                status: '',
                transaction_type: '',
                network: ''
            });
            const data = response.data?.data?.transactions || [];
            setTransactions(data);
            setFilteredTransactions(data);
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to fetch transactions");
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = transactions.filter((transaction) => {
            const matchesSearch = 
                transaction.user_id?.email?.toLowerCase().includes(search.toLowerCase()) ||
                transaction.transaction_hash?.toLowerCase().includes(search.toLowerCase()) ||
                transaction.description?.toLowerCase().includes(search.toLowerCase()) ||
                transaction.network?.toLowerCase().includes(search.toLowerCase());

            const matchesType = !filters.transaction_type || 
                transaction.transaction_category === filters.transaction_type;

            const matchesStatus = !filters.status || 
                transaction.status === filters.status;

            const matchesNetwork = !filters.network || 
                transaction.network === filters.network;

            return matchesSearch && matchesType && matchesStatus && matchesNetwork;
        });

        setFilteredTransactions(filtered);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending':
                return <AlertCircle className="w-4 h-4 text-warning" />;
            case 'approved':
                return <CheckCircle className="w-4 h-4 text-success" />;
            case 'cancelled':
                return <XCircle className="w-4 h-4 text-danger" />;
            default:
                return <Clock className="w-4 h-4 text-secondary" />;
        }
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB"); // Format: DD/MM/YYYY
    };

    const truncateHash = (hash) => {
        if (!hash) return 'N/A';
        return hash.length > 20 ? `${hash.substring(0, 10)}...${hash.substring(hash.length - 10)}` : hash;
    };

    const columns = [
        {
            name: "S. No.",
            selector: (row, index) => index + 1,
            sortable: true,
            width: "80px"
        },
        {
            name: "Transaction Type",
            cell: (row) => (
                <div className="d-flex flex-column">
                    <div className="d-flex align-items-center gap-2">
                        {row.transaction_category === 'topup' ? (
                            <DollarSign className="w-4 h-4 text-success" />
                        ) : (
                            <User className="w-4 h-4 text-primary" />
                        )}
                        <span className="fw-medium text-capitalize">
                            {row.transaction_category}
                        </span>
                    </div>
                    {row.transaction_category === 'topup' && (
                        <small className="text-muted">
                            {row.network}
                        </small>
                    )}
                    {row.transaction_category === 'wallet' && (
                        <small className="text-muted text-capitalize">
                            {row.transaction_type}
                        </small>
                    )}
                </div>
            ),
            sortable: true,
            width: "150px"
        },
        {
            name: "User Email",
            selector: (row) => row.user_id?.email || 'N/A',
            sortable: true,
            width: "200px"
        },
        {
            name: "Amount",
            selector: (row) => formatAmount(row.amount),
            sortable: true,
            width: "120px"
        },
        {
            name: "Status",
            cell: (row) => {
                const badgeClass = {
                    pending: "bg-warning",
                    approved: "bg-success",
                    cancelled: "bg-danger",
                }[row.status] || "bg-secondary";

                return (
                    <div className="d-flex align-items-center gap-2">
                        {row.status && (
                            <>
                                {getStatusIcon(row.status)}
                                <span className={`badge ${badgeClass}`}>
                                    {row.status}
                                </span>
                            </>
                        )}
                        {row.transaction_type && !row.status && (
                            <span className="badge bg-info">
                                {row.transaction_type}
                            </span>
                        )}
                    </div>
                );
            },
            sortable: true,
            width: "130px"
        },
        {
            name: "Date",
            cell: (row) => (
                <div className="d-flex align-items-center gap-1">
                    <Calendar className="w-4 h-4 text-muted" />
                    <span>{formatDate(row.date)}</span>
                </div>
            ),
            sortable: true,
            width: "150px"
        },
        {
            name: "Transaction Hash",
            cell: (row) => (
                <div className="d-flex flex-column gap-1">
                    {row.transaction_hash && (
                        <div className="d-flex align-items-center gap-1">
                            <Hash className="w-3 h-3 text-muted" />
                            <span className="font-monospace small">
                                {truncateHash(row.transaction_hash)}
                            </span>
                        </div>
                    )}
                    {row.description && (
                        <div className="small text-muted" style={{maxWidth: '200px'}}>
                            {row.description.length > 50 ? 
                                `${row.description.substring(0, 50)}...` : 
                                row.description
                            }
                        </div>
                    )}
                    {row.deposit_wallet_address && (
                        <div className="small text-muted font-monospace">
                            {truncateHash(row.deposit_wallet_address)}
                        </div>
                    )}
                </div>
            ),
            sortable: true,
            width: "250px"
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="btn-group" role="group">
                    <Link
                        to={`/admin/transaction/details/${row._id}`}
                        className="btn btn-outline-info btn-sm"
                    >
                        <i className="icofont-eye text-info"></i>
                    </Link>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "100px"
        },
    ];

    return (
        <div className="body d-flex py-3">
            <div className="container-xxl">
                <div className="row align-items-center">
                    <div className="border-0 mb-4">
                        <div className="card-header py-3 no-bg bg-transparent d-flex align-items-center px-0 justify-content-between border-bottom flex-wrap">
                            <h3 className="fw-bold mb-0">Transaction History</h3>
                        </div>
                    </div>
                </div>
                <div className="row align-item-center">
                    <div className="col-md-12">
                        <div className="card mb-3">
                            <div className="card-header py-3 bg-transparent border-bottom-0 d-flex justify-content-between">
                                <h6 className="mb-0 fw-bold">Transactions</h6>
                            </div>
                            <div className="card-body">
                                {/* Search and Filters */}
                                <div className="row mb-3">
                                    <div className="col-md-3">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search by email, hash, or description"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <select
                                            value={filters.transaction_type}
                                            onChange={(e) => handleFilterChange('transaction_type', e.target.value)}
                                            className="form-select"
                                        >
                                            <option value="">All Types</option>
                                            <option value="topup">Top-up</option>
                                            <option value="wallet">Wallet</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <select
                                            value={filters.status}
                                            onChange={(e) => handleFilterChange('status', e.target.value)}
                                            className="form-select"
                                        >
                                            <option value="">All Status</option>
                                            <option value="pending">Pending</option>
                                            <option value="approved">Approved</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <select
                                            value={filters.network}
                                            onChange={(e) => handleFilterChange('network', e.target.value)}
                                            className="form-select"
                                        >
                                            <option value="">All Networks</option>
                                            <option value="BTC">BTC</option>
                                            <option value="ETH">ETH</option>
                                            <option value="USDT_TRC20">USDT TRC20</option>
                                            <option value="USDT_ERC20">USDT ERC20</option>
                                        </select>
                                    </div>
                                </div>

                                {loading ? (
                                    <div className="d-flex justify-content-center align-items-center" style={{height: '200px'}}>
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <DataTable
                                        columns={columns}
                                        data={filteredTransactions}
                                        pagination
                                        highlightOnHover
                                        noDataComponent="No transactions found"
                                        paginationPerPage={10}
                                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TransactionListing;